import { Link } from '@solidjs/meta';
import { decode as decodeBlurHash } from 'blurhash';
import { createEffect, createMemo, createSignal, Show, splitProps } from 'solid-js';
import { cn } from 'tailwind-variants';
import type { JSX } from 'solid-js';

type Props = Omit<JSX.ImgHTMLAttributes<HTMLImageElement>, 'sizes' | 'src'> & {
	sizes: string;
	width: number;
	height: number;
	preload?: boolean;
	src: string;
	blurhash?: string;
	wrapperClass?: string;
};

export function Image(props: Props) {
	const [, spread] = splitProps(props, ['blurhash', 'class']);
	const [canvasRef, setCanvasRef] = createSignal<HTMLCanvasElement>();

	createEffect(() => {
		const ctx = canvasRef()?.getContext('2d');
		if (!ctx || !props.blurhash) {
			return;
		}
		const imgData = ctx.createImageData(props.width, props.height);
		imgData.data.set(decodeBlurHash(props.blurhash, props.width, props.height));
		ctx.putImageData(imgData, 0, 0);
	});

	return (
		<div class={cn('relative overflow-hidden', props.wrapperClass)}>
			<Show when={props.fetchpriority === 'high' && props.src}>
				{(url) => (
					<Show
						when={props.srcset}
						fallback={<Link rel="preload" fetchpriority="high" as="image" href={url()} crossorigin="anonymous" />}
					>
						<Link
							rel="preload"
							fetchpriority="high"
							as="image"
							href={url()}
							imagesrcset={props.srcset}
							imagesizes={props.sizes || '100vw'}
							crossorigin="anonymous"
						/>
					</Show>
				)}
			</Show>
			<Show when={props.blurhash}>
				<canvas
					role="img"
					aria-label={props.alt ?? ''}
					ref={setCanvasRef}
					class="absolute inset-0 -z-10 size-full"
					width={props.width}
					height={props.height}
				/>
			</Show>
			<img
				{...spread}
				srcset={props.srcset}
				sizes={props.sizes || '100vw'}
				src={props.src}
				alt={props.alt ?? ''}
				crossorigin="anonymous"
				class={props.class}
			/>
		</div>
	);
}

type CloudflareProps = Props & {
	/**
	 * Whether to preserve animation frames from input files. Default is true. Setting it to false reduces animations to still images. This setting is recommended when enlarging images or processing arbitrary user content, because large GIF animations can weigh tens or even hundreds of megabytes. It is also useful to set anim:false when using format:"json" to get the response quicker without the number of frames.
	 */
	anim?: boolean;
	/**
	 * Background color to add underneath the image. Applies to images with transparency (for example, PNG) and images resized with `fit=pad`. Accepts any CSS color using CSS4 modern syntax, such as `rgb(255 255 0)` and `rgba(255 255 0 100)`.
	 */
	background?: string;
	/**
	 * Blur radius between `1` (slight blur) and `250` (maximum). Be aware that you cannot use this option to reliably obscure image content, because savvy users can modify an image's URL and remove the blur option. Use Workers to control which options can be set.
	 */
	blur?: number;
	/**
	 * Increase brightness by a factor. A value of 1.0 equals no change, a value of `0.5` equals half brightness, and a value of `2.0` equals twice as bright. `0` is ignored.
	 */
	brightness?: number;
	/**
	 * Slightly reduces latency on a cache miss by selecting a quickest-to-compress file format, at a cost of increased file size and lower image quality. It will usually override the `format` option and choose JPEG over WebP or AVIF. We do not recommend using this option, except in unusual circumstances like resizing uncacheable dynamically-generated images.
	 */
	compression?: 'fast';
	/**
	 * Increase contrast by a factor. A value of `1.0` equals no change, a value of `0.5` equals low contrast, and a value of `2.0` equals high contrast. 0 is ignored.
	 */
	contrast?: number;
	/**
	 * Device Pixel Ratio. Default is `1`. Multiplier for `width`/`height` that makes it easier to specify higher-DPI sizes in `<img srcset>`.
	 */
	dpr?: number;
	/**
	 * Affects interpretation of `width` and `height`. All resizing modes preserve aspect ratio. Used as a string in Workers integration. Available modes are:
	 * - `scale-down`
	 * 	 Similar to `contain`, but the image is never enlarged. If the image is larger than given `width` or `height`, it will be resized. Otherwise its original size will be kept.
	 * - `contain`
	 *    Image will be resized (shrunk or enlarged) to be as large as possible within the given `width` or `height` while preserving the aspect ratio. If you only provide a single dimension (for example, only `width`), the image will be shrunk or enlarged to exactly match that dimension.
	 * - `cover`
	 *    Resizes (shrinks or enlarges) to fill the entire area of `width` and `height`. If the image has an aspect ratio different from the ratio of `width` and `height`, it will be cropped to fit.
	 * - `crop`
	 *    Image will be shrunk and cropped to fit within the area specified by `width` and `height`. The image will not be enlarged. For images smaller than the given dimensions, it is the same as `scale-down`. For images larger than the given dimensions, it is the same as `cover`. See also `trim`
	 * - `pad`
	 *    Resizes to the maximum size that fits within the given `width` and `height`, and then fills the remaining area with a background color (white by default). This mode is not recommended, since you can achieve the same effect more efficiently with the contain mode and the CSS `object-fit: contain` property.
	 * - `squeeze`
	 *    Resizes the image to the exact `width` and `height` specified. This mode does not preserve the original aspect ratio and will cause the image to appear stretched or squashed.
	 */
	fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad' | 'squeeze';
	/**
	 * Flips the image horizontally, vertically, or both. Can be used with the rotate parameter to set the orientation of an image.
	 *
	 * Flipping is performed before rotation. For example, if you apply `flip=h,rotate=90`, then the image will be flipped horizontally, then rotated by 90 degrees.
	 *
	 * Available options are:
	 * - `h`: Flips the image horizontally.
	 * - `v`: Flips the image vertically.
	 * - `hv`: Flips the image vertically and horizontally.
	 */
	flip?: 'h' | 'v' | 'hv';
	/**
	 * The auto option will serve the WebP or AVIF format to browsers that support it. If this option is not specified, a standard format like JPEG or PNG will be used. Cloudflare will default to JPEG when possible due to the large size of PNG files.
	 */
	format?: 'avif' | 'webp' | 'jpeg' | 'baseline-jpeg' | 'auto';
	/**
	 * Specifies how an image should be cropped when used with fit=cover and fit=crop. Available options are auto, face, a side (left, right, top, bottom), and relative coordinates (XxY with a valid range of 0.0 to 1.0):
	 *
	 * - `auto`
	 * 	  Selects focal point based on saliency detection (using maximum symmetric surround algorithm).
	 * - `side`
	 *    A side ("left", "right", "top", "bottom") or coordinates specified on a scale from 0.0 (top or left) to 1.0 (bottom or right), 0.5 being the center. The X and Y coordinates are separated by lowercase x in the URL format. For example, 0x1 means left and bottom, 0.5x0.5 is the center, 0.5x0.33 is a point in the top third of the image.
	 * - `face`
	 *    Automatically sets the focal point based on detected faces in an image. This can be combined with the zoom parameter to specify how closely the image should be cropped towards the faces.
	 *    The new focal point is determined by a minimum bounding box that surrounds all detected faces. If no faces are found, then the focal point will fall back to the center of the image.
	 */
	gravity?: 'auto' | 'side' | 'face' | 'left' | 'right' | 'top' | 'bottom' | `${number}x${number}`;
	height: number;
	/**
	 * Specifies quality for images in JPEG, WebP, and AVIF formats. The quality is in a 1-100 scale, but useful values are between `50` (low quality, small file size) and `90` (high quality, large file size). `85` is the default. When using the PNG format, an explicit quality setting allows use of PNG8 (palette) variant of the format. Use the `format=auto` option to allow use of WebP and AVIF formats.
	 */
	quality?: number | 'high' | 'medium-high' | 'medium-low' | 'low';
	/**
	 * Number of degrees (`90`, `180`, or `270`) to rotate the image by. `width` and `height` options refer to axes after rotation.
	 */
	rotate?: number;
	/**
	 * Automatically isolates the subject of an image by replacing the background with transparent pixels.
	 */
	segment?: 'foreground';
	width: number;
};

const availableSizes = [120, 240, 480, 640, 750, 828, 1080, 1200, 1440, 1680, 1920, 2048, 2560, 3840];

export function CloudflareImage(props: CloudflareProps) {
	const [transformProps, imgProps] = splitProps(props, [
		'anim',
		'background',
		'blur',
		'brightness',
		'compression',
		'contrast',
		'dpr',
		'fit',
		'flip',
		'format',
		'gravity',
		'quality',
		'rotate',
		'segment',
	]);

	const sources = createMemo(() => {
		const aspect = props.width / props.height;
		const srcset: Array<string> = [];
		let first: string | undefined;

		for (const width of availableSizes) {
			const url = new URL(imgProps.src);
			const height = Math.round(width / aspect);
			const transforms = Object.entries({ quality: 50, ...transformProps }).map(([key, val]) => `${key}=${val}`);
			url.pathname = `/cdn-cgi/image/width=${width},height=${height},${transforms.join(',')}${url.pathname}`;
			srcset.push(`${url.toString()} ${width}w`);
			first ??= url.toString();
		}

		return { srcset: srcset.join(','), url: first! };
	});

	return <Image {...imgProps} src={sources().url} srcset={sources().srcset} />;
}
